import '../style.css';
import CurlSnippet from '../../InnerComponents';
import { ErpDocumentationMap } from '../constants';

export const documentation_v0 = (
  <>
    <div className="my-3">
      <h1>Integração de ERP V0</h1>
      <p>A integração é composta por três fluxos:</p>
      <ol>
        <li>
          O primeiro é um webhook para assinar e receber novos pedidos criados
          no ambiente Inventa.
        </li>
        <li>
          O segundo é uma API para receber as atualizações de pedido. Quando
          algum pedido é alterado no ERP do fornecedor esta API deve ser chamada
          para atualizar este pedido no ambiente Inventa. A API permite também
          consultar pedidos de um fornecedor integrado usando o supplier ID
          correspondente. A consulta inclui a possibilidade de filtrar por data
          de criação ou de atualização dos pedidos.
        </li>
        <li>
          O terceiro é uma API para receber atualizações de produto. Quando
          algum produto é alterado no ERP do fornecedor esta API deve ser
          chamada para atualizar este produto no ambiente Inventa.
        </li>
      </ol>
    </div>

    <div className="my-3">
      <h2>Webhook de pedidos</h2>
      <p>
        A URL da API que vai receber a requisição é persistida internamente de
        forma manual. Então quando algum fornecedor quiser receber os pedidos
        ele precisa enviar ao seu representante na Inventa a URL e uma API_KEY
        para autenticação na mesma.
      </p>
      <p>
        Para criar/atualizar o pedido, o serviço da Inventa enviará uma
        requisição POST/PUT para a URL cadastrada utilizando a chave de
        API(API_KEY) no cabeçalho da requisição e um payload JSON no corpo da
        requisição.
      </p>
      <p>
        Nota: Requisição POST para criar e requisição PUT para atualizar. O
        status pode ser TO_BE_CONFIRMED, UNDER_REVIEW, CONFIRMED, NF_RECEIVED,
        SHIPPED, SHIPPING_CONFIRMED, DELIVERED, CANCELLED.
      </p>

      <CurlSnippet {...ErpDocumentationMap.webhookOrder} />
    </div>

    <div className="my-3">
      <h2>API de Produto</h2>
      <p>
        Este endpoint é responsável por receber atualizações de produtos
        provenientes do ERP do fornecedor.
      </p>
      <p>
        Quando o fornecedor for integrado, a equipe de integração dará ao
        fornecedor uma API_KEY para que ele consiga acessar o endpoint [PUT]
        https://integration.inventa.tec.br/api/products/.
      </p>
      <p>
        Na primeira versão a API atualizará apenas o status do produto e o
        estoque.
      </p>
      <p>
        Esse endpoint pode atualizar um único produto ou vários em massa. Veja
        abaixo um exemplo para cada caso.
      </p>

      <div className="my-3">
        <h3>Atualização de um único produto</h3>
        <p>
          Para atualizar o produto, precisamos usar o método PUT e passar o
          product_id(SKU) no endpoint, por exemplo [PUT]
          https://integration.inventa.tec.br/api/products/product-id/. O
          endpoint receberá a API_KEY no cabeçalho da requisição e os dados a
          serem atualizados no payload JSON no corpo, como CURL abaixo.
        </p>
        <p>
          Nota: O status é opcional. Pode ser ATIVO, pode vender ou INATIVO, não
          aparece no site.
        </p>

        <CurlSnippet {...ErpDocumentationMap.productUpdate} />
      </div>

      <div className="my-3">
        <h3>Atualização do produto em massa</h3>
        <p>
          Para atualizar os produtos em massa, precisamos usar o método PUT e
          passar no endpoint como [PUT]
          https://integration.inventa.tec.br/api/products/. O endpoint receberá
          a API_KEY no cabeçalho da requisição e os dados para atualizar no
          payload JSON no corpo como CURL abaixo.
        </p>
        <p>
          Nota: O status é opcional. Pode ser ATIVO, pode vender ou INATIVO, não
          aparece no site.
        </p>

        <CurlSnippet {...ErpDocumentationMap.batchProductUpdate} />
      </div>
    </div>

    <div className="my-3">
      <h2>API de Pedidos</h2>
      <p>
        Este endpoint é responsável por receber atualizações de pedidos
        provenientes do ERP do fornecedor e por acessar pedidos cadastrados na
        Inventa.
      </p>
      <p>
        Quando o fornecedor for integrado a nós, a equipe de integração
        fornecerá ao fornecedor uma API_KEY que pode acessar o endpoint [PUT]
        https://integration.inventa.tec.br/api/orders/
      </p>
      <p>
        Na primeira versão, a API atualizará apenas o status do pedido, as
        informações da fatura e as atualizações de envio.
      </p>
      <p>
        Esse endpoint pode atualizar um único pedido ou em massa. Veja abaixo um
        exemplo para cada caso.
      </p>

      <div className="my-3">
        <h3>Atualização de um único pedido</h3>
        <p>
          Para atualizar um único pedido, precisamos usar o método PUT e passar
          o order_id(NUMBER) no endpoint como [PUT]
          https://integration.inventa.tec.br/api/orders/order-id/. O endpoint
          receberá a API_KEY no cabeçalho da requisição e os dados a serem
          atualizados no payload JSON no corpo, como CURL abaixo.
        </p>
        <p>
          Nota: O status pode ser TO_BE_CONFIRMED, UNDER_REVIEW, CONFIRMED,
          NF_RECEIVED, SHIPPED, SHIPPING_CONFIRMED, DELIVERED, CANCELLED.
        </p>

        <CurlSnippet {...ErpDocumentationMap.orderUpdate} />
      </div>

      <div className="my-3">
        <h3>Atualização de pedidos em massa</h3>
        <p>
          Para atualizar os pedidos em massa, precisamos usar o método PUT como
          por exemplo [PUT] https://integration.inventa.tec.br/api/orders/. O
          endpoint receberá a API_KEY no cabeçalho da requisição e os dados para
          atualizar no payload JSON no corpo como CURL abaixo.
        </p>
        <p>
          Nota: O status pode ser TO_BE_CONFIRMED, UNDER_REVIEW, CONFIRMED,
          NF_RECEIVED, SHIPPED, SHIPPING_CONFIRMED, DELIVERED, CANCELLED.
        </p>

        <CurlSnippet {...ErpDocumentationMap.batchOrderUpdate} />
      </div>

      <div className="my-3">
        <h3>Acesso de um único pedido</h3>
        <p>
          Para acessar um único pedido, precisamos usar o método GET e passar um
          o order_id(NUMBER) no endpoint como [GET]
          https://integration.inventa.tec.br/api/orders/order-id/. O endpoint
          receberá a API_KEY no cabeçalho da requisição como CURL abaixo.
        </p>

        <CurlSnippet {...ErpDocumentationMap.getOrder} />
      </div>

      <div className="my-3">
        <h3>Buscar pedidos por data de criação</h3>
        <p>
          Para acessar pedidos por data de criação, precisamos usar o método GET
          e passar no endpoint como parâmetro da requisição o supplier_id(CNPJ)
          e uma data de referência por exemplo [GET]
          https://integration.inventa.tec.br/api/orders?supplier_id=cnpj&created_at=2022-05-07.
          O endpoint receberá a API_KEY no cabeçalho da requisição como CURL
          abaixo.
        </p>

        <CurlSnippet {...ErpDocumentationMap.searchOrderbyCreatedAtDate} />
      </div>

      <div className="my-3">
        <h3>Buscar pedidos por data de atualização</h3>
        <p>
          Para acessar pedidos por data de criação, precisamos usar o método GET
          e passar no endpoint como parâmetro da requisição o supplier_id(CNPJ)
          e uma data de referência por exemplo [GET]
          https://integration.inventa.tec.br/api/orders?supplier_id=cnpj&updated_at=2022-05-07.
          O endpoint receberá a API_KEY no cabeçalho da requisição como CURL
          abaixo.
        </p>

        <CurlSnippet {...ErpDocumentationMap.searchOrderbyUpdatedAtDate} />
      </div>
    </div>
  </>
);
